import { useId, useRef } from 'react'
import { Button } from './Button'
import styles from './FileUploadField.module.css'

function getFileSummary({ filename, files, multiple }) {
  if (filename) return filename
  if (!files) return ''

  if (Array.isArray(files)) {
    if (!files.length) return ''
    if (files.length === 1) return files[0]?.name || files[0]
    return `${files.length} files selected`
  }

  if (files instanceof FileList) {
    if (!files.length) return ''
    if (files.length === 1) return files[0]?.name || ''
    return `${files.length} files selected`
  }

  if (multiple && typeof files === 'string') {
    return files
  }

  return typeof files === 'string' ? files : ''
}

export function FileUploadField({
  label,
  accept,
  multiple = false,
  onChange,
  helper,
  error,
  pending = false,
  disabled = false,
  variant = 'inline',
  compact = false,
  triggerLabel = 'Choose file',
  clearLabel = 'Remove',
  filename,
  files,
  previewSrc,
  previewAlt = '',
  placeholderText,
  onClear,
  className = '',
}) {
  const inputId = useId()
  const inputRef = useRef(null)
  const fileSummary = getFileSummary({ filename, files, multiple })
  const classes = [
    styles.field,
    styles[variant] || styles.inline,
    compact ? styles.compact : '',
    className,
  ].filter(Boolean).join(' ')

  const openPicker = () => {
    if (!disabled) {
      inputRef.current?.click()
    }
  }

  return (
    <div className={classes}>
      {label ? <label htmlFor={inputId} className={styles.label}>{label}</label> : null}
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={onChange}
        className={styles.input}
        disabled={disabled}
      />

      {variant === 'avatar' ? (
        <div className={styles.avatarLayout}>
          <div className={styles.avatarPreview}>
            {previewSrc ? (
              <img src={previewSrc} alt={previewAlt} className={styles.avatarImage} />
            ) : (
              <span className={styles.avatarPlaceholder}>{placeholderText || '?'}</span>
            )}
          </div>
          <div className={styles.body}>
            <div className={styles.actions}>
              <Button
                type="button"
                variant="secondary"
                onClick={openPicker}
                disabled={disabled || pending}
              >
                {triggerLabel}
              </Button>
              {onClear ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onClear}
                  disabled={disabled || pending}
                >
                  {clearLabel}
                </Button>
              ) : null}
            </div>
            {fileSummary ? <span className={styles.filename}>{fileSummary}</span> : null}
            {helper ? <p className={styles.helper}>{helper}</p> : null}
            {error ? <p className={styles.error}>{error}</p> : null}
          </div>
        </div>
      ) : (
        <div className={styles.body}>
          <div className={styles.actions}>
            <Button
              type="button"
              variant="secondary"
              onClick={openPicker}
              disabled={disabled || pending}
            >
              {triggerLabel}
            </Button>
            {fileSummary ? <span className={styles.filename}>{fileSummary}</span> : null}
            {onClear ? (
              <Button
                type="button"
                variant="ghost"
                onClick={onClear}
                disabled={disabled || pending}
              >
                {clearLabel}
              </Button>
            ) : null}
          </div>
          {helper ? <p className={styles.helper}>{helper}</p> : null}
          {error ? <p className={styles.error}>{error}</p> : null}
          {variant === 'asset' ? (
            <div className={styles.assetHint}>
              {previewSrc ? (
                <span className={styles.previewActive}>Preview ready</span>
              ) : (
                <span>Images and videos stay review-only until you save them.</span>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
